import './style.less';

import React, { useMemo, useRef } from 'react';
import ImageGallery from 'react-image-gallery';
import { SRLWrapper, useLightbox } from 'simple-react-lightbox';

import rightNav from '_images/carousel-right-nav.png';
import { urlFor } from '_utils/images';

const MAX_THUMBNAILS_TO_SHOW = 4;

function ImageCarousel({ sourceImages, sourceType = 'sanity' }) {
  const galleryRef = useRef();
  const { openLightbox } = useLightbox();

  const images = useMemo(() => {
    if (!sourceImages) return [];
    if (sourceType === 'sanity') {
      return sourceImages.map((sanityImage) => ({
        original: urlFor(sanityImage)?.format('webp').url(),
        thumbnail: urlFor(sanityImage)?.format('webp').width(200).url(),
        originalWidth: sanityImage?.width,
        originalHeight: sanityImage?.height,
      }));
    }

    if (sourceType === 'oneshift-legacy') {
      return sourceImages.map((img) => ({
        original: img.link,
        thumbnail: img.thumb_link,
      }));
    }
  }, [sourceImages, sourceType]);

  const imagesForCarousel = images.slice(0, MAX_THUMBNAILS_TO_SHOW + 1);

  return (
    <div>
      <ImageGallery
        ref={galleryRef}
        onClick={openLightbox}
        items={imagesForCarousel}
        showPlayButton={false}
        showFullscreenButton={false}
        renderLeftNav={(onClick) => (
          <button
            type="button"
            onClick={onClick}
            className="absolute z-10 image-gallery-left-nav sm:-left-7"
          >
            <div className="w-10 h-10 flex items-center justify-center shadow-nav-button rounded-full bg-white-75">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="w-7 h-7 text-content-secondary"
              >
                <path
                  fillRule="evenodd"
                  d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </button>
        )}
        renderRightNav={(onClick) => (
          <button
            type="button"
            onClick={onClick}
            className="absolute z-10 image-gallery-right-nav sm:-right-7"
          >
            <div className="w-10 h-10 flex items-center justify-center shadow-nav-button rounded-full bg-white-75">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="w-7 h-7 text-content-secondary"
              >
                <path
                  fillRule="evenodd"
                  d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          </button>
        )}
        showThumbnails={false}
        renderItem={(item) => (
          <div className="flex justify-center">
            <img
              src={item.original}
              alt={item.original}
              className="sm:rounded-lg w-full h-full object-cover object-right-bottom"
            />
          </div>
        )}
      />
      {imagesForCarousel.length > 1 ? (
        <div className="carousel-thumbnails-wrapper">
          {imagesForCarousel.map((image, index) => {
            if (index < MAX_THUMBNAILS_TO_SHOW) {
              return (
                <div
                  key={index}
                  className="carousel-thumbnail cursor-pointer"
                  onClick={() => galleryRef.current.slideToIndex(index)}
                  role="button"
                >
                  <img src={image.thumbnail} alt="thumbnail" />
                </div>
              );
            }
            return (
              <div
                key={index}
                className="carousel-thumbnail cursor-pointer last-item"
                onClick={openLightbox}
              >
                <div className="more-images-overlay" />
                <div className="more-images">
                  <span>{`+${images.length - MAX_THUMBNAILS_TO_SHOW}`}</span>
                  <span className="ml-1 more-text">more</span>
                </div>
                <img src={image.thumbnail} alt="thumbnail" />
              </div>
            );
          })}
        </div>
      ) : null}

      <SRLWrapper
        elements={images.map((image) => ({
          src: image.original,
          thumbnail: image.thumbnail,
        }))}
      />
    </div>
  );
}

export default ImageCarousel;
